/**
 * 13F institution holdings utility
 *
 * SEC EDGAR fetching and XML parsing is done by /api/stock/13f (Cloudflare Pages Function).
 * This module handles comparison/metrics calculation on the frontend side.
 */

import {
  buildSectorDistribution,
  buildValueTrend,
  compareHoldings,
  summarizeHoldingChanges,
} from './portfolioMetrics';

const INSTITUTIONS = [
  {
    cik: '0001067983',
    name: '伯克希尔·哈撒韦',
    nameEn: 'Berkshire Hathaway',
    short: 'BRK',
    desc: '巴菲特掌舵的多元化控股集团，以长期价值投资著称',
    descEn: 'Warren Buffett-led holding company known for long-term value investing',
  },
  {
    cik: '0001166559',
    name: '桥水基金',
    nameEn: 'Bridgewater Associates',
    short: 'BW',
    desc: '全球最大对冲基金，创始人达利欧，以宏观策略闻名',
    descEn: 'Macro-focused hedge fund founded by Ray Dalio',
  },
  {
    cik: '0000040645',
    name: '高盛集团',
    nameEn: 'Goldman Sachs',
    short: 'GS',
    desc: '华尔街顶级投行，全球并购与交易龙头',
    descEn: 'Leading Wall Street investment bank and global trading firm',
  },
  {
    cik: '0001364742',
    name: '贝莱德',
    nameEn: 'BlackRock',
    short: 'BLK',
    desc: '全球最大资产管理公司，管理资产超 10 万亿美元',
    descEn: 'World-leading asset manager with over $10T in assets under management',
  },
  {
    cik: '0000031678',
    name: '先锋集团',
    nameEn: 'Vanguard Group',
    short: 'VG',
    desc: '指数投资先驱，低成本 ETF 和被动投资倡导者',
    descEn: 'Index investing pioneer and advocate for low-cost passive funds',
  },
  {
    cik: '0001756111',
    name: 'ARK 投资管理',
    nameEn: 'ARK Investment Management',
    short: 'ARK',
    desc: '凯瑟琳·伍德创立，专注颠覆性创新主题投资',
    descEn: 'Cathie Wood-founded manager focused on disruptive innovation',
  },
];

const FALLBACK_HOLDINGS = {
  '0001067983': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'APPLE INC', titleOfClass: 'COM', cusip: '037833100', value: 69700000, shares: 300000000, shareType: 'SH', weight: '23.90' },
      { nameOfIssuer: 'AMERICAN EXPRESS CO', titleOfClass: 'COM', cusip: '025816109', value: 41100000, shares: 151610700, shareType: 'SH', weight: '14.09' },
      { nameOfIssuer: 'BANK OF AMERICA CORP', titleOfClass: 'COM', cusip: '060505104', value: 33400000, shares: 680233587, shareType: 'SH', weight: '11.45' },
      { nameOfIssuer: 'COCA COLA CO', titleOfClass: 'COM', cusip: '191216100', value: 28900000, shares: 400000000, shareType: 'SH', weight: '9.91' },
      { nameOfIssuer: 'CHEVRON CORP', titleOfClass: 'COM', cusip: '166764100', value: 18700000, shares: 118610534, shareType: 'SH', weight: '6.41' },
    ],
  },
  '0001166559': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'ISHARES CORE MSCI EMERGING MARKETS ETF', titleOfClass: 'ETF', cusip: '46434G103', value: 1330000, shares: 28900000, shareType: 'SH', weight: '8.10' },
      { nameOfIssuer: 'SPDR S&P 500 ETF TRUST', titleOfClass: 'ETF', cusip: '78462F103', value: 1210000, shares: 2100000, shareType: 'SH', weight: '7.37' },
      { nameOfIssuer: 'ISHARES MSCI EAFE ETF', titleOfClass: 'ETF', cusip: '464287465', value: 970000, shares: 11000000, shareType: 'SH', weight: '5.91' },
      { nameOfIssuer: 'PROCTER & GAMBLE CO', titleOfClass: 'COM', cusip: '742718109', value: 690000, shares: 4100000, shareType: 'SH', weight: '4.20' },
      { nameOfIssuer: 'COCA COLA CO', titleOfClass: 'COM', cusip: '191216100', value: 610000, shares: 8400000, shareType: 'SH', weight: '3.72' },
    ],
  },
  '0000040645': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'SPDR S&P 500 ETF TRUST', titleOfClass: 'ETF', cusip: '78462F103', value: 9100000, shares: 15800000, shareType: 'SH', weight: '6.20' },
      { nameOfIssuer: 'MICROSOFT CORP', titleOfClass: 'COM', cusip: '594918104', value: 8200000, shares: 17800000, shareType: 'SH', weight: '5.58' },
      { nameOfIssuer: 'NVIDIA CORP', titleOfClass: 'COM', cusip: '67066G104', value: 7600000, shares: 62000000, shareType: 'SH', weight: '5.17' },
      { nameOfIssuer: 'APPLE INC', titleOfClass: 'COM', cusip: '037833100', value: 7200000, shares: 31000000, shareType: 'SH', weight: '4.90' },
      { nameOfIssuer: 'AMAZON COM INC', titleOfClass: 'COM', cusip: '023135106', value: 5900000, shares: 27000000, shareType: 'SH', weight: '4.01' },
    ],
  },
  '0001364742': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'MICROSOFT CORP', titleOfClass: 'COM', cusip: '594918104', value: 95200000, shares: 206000000, shareType: 'SH', weight: '5.80' },
      { nameOfIssuer: 'NVIDIA CORP', titleOfClass: 'COM', cusip: '67066G104', value: 87900000, shares: 716000000, shareType: 'SH', weight: '5.36' },
      { nameOfIssuer: 'APPLE INC', titleOfClass: 'COM', cusip: '037833100', value: 86100000, shares: 370000000, shareType: 'SH', weight: '5.25' },
      { nameOfIssuer: 'AMAZON COM INC', titleOfClass: 'COM', cusip: '023135106', value: 50600000, shares: 232000000, shareType: 'SH', weight: '3.09' },
      { nameOfIssuer: 'META PLATFORMS INC', titleOfClass: 'COM', cusip: '30303M102', value: 39200000, shares: 62000000, shareType: 'SH', weight: '2.39' },
    ],
  },
  '0000031678': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'MICROSOFT CORP', titleOfClass: 'COM', cusip: '594918104', value: 110500000, shares: 239000000, shareType: 'SH', weight: '6.03' },
      { nameOfIssuer: 'NVIDIA CORP', titleOfClass: 'COM', cusip: '67066G104', value: 104300000, shares: 849000000, shareType: 'SH', weight: '5.69' },
      { nameOfIssuer: 'APPLE INC', titleOfClass: 'COM', cusip: '037833100', value: 98000000, shares: 421000000, shareType: 'SH', weight: '5.35' },
      { nameOfIssuer: 'AMAZON COM INC', titleOfClass: 'COM', cusip: '023135106', value: 60400000, shares: 277000000, shareType: 'SH', weight: '3.30' },
      { nameOfIssuer: 'META PLATFORMS INC', titleOfClass: 'COM', cusip: '30303M102', value: 46900000, shares: 74000000, shareType: 'SH', weight: '2.56' },
    ],
  },
  '0001756111': {
    filingDate: '2026-02-14',
    holdings: [
      { nameOfIssuer: 'TESLA INC', titleOfClass: 'COM', cusip: '88160R101', value: 1220000, shares: 3600000, shareType: 'SH', weight: '9.20' },
      { nameOfIssuer: 'COINBASE GLOBAL INC', titleOfClass: 'COM', cusip: '19260Q107', value: 980000, shares: 3500000, shareType: 'SH', weight: '7.39' },
      { nameOfIssuer: 'ROKU INC', titleOfClass: 'COM', cusip: '77543R102', value: 710000, shares: 8200000, shareType: 'SH', weight: '5.35' },
      { nameOfIssuer: 'ROBINHOOD MARKETS INC', titleOfClass: 'COM', cusip: '770700102', value: 680000, shares: 13800000, shareType: 'SH', weight: '5.12' },
      { nameOfIssuer: 'BLOCK INC', titleOfClass: 'COM', cusip: '852234103', value: 620000, shares: 7200000, shareType: 'SH', weight: '4.67' },
    ],
  },
};

function cikPadded(cik) {
  return cik.replace(/^0+/, '').padStart(10, '0');
}

function fallbackInstitutionHoldings(cik) {
  const data = FALLBACK_HOLDINGS[cikPadded(cik)];
  if (!data) return null;
  const holdings = data.holdings.map((holding) => ({ ...holding }));
  const totalValue = holdings.reduce((sum, holding) => sum + holding.value, 0);
  return {
    filingDate: data.filingDate,
    totalValue,
    holdings,
    changeSummary: null,
    previousFilingDate: null,
    sectorDistribution: buildSectorDistribution(holdings),
    trend: buildValueTrend([{ filingDate: data.filingDate, totalValue }]),
    isFallback: true,
  };
}

/**
 * Fetch institution holdings via backend API.
 * Returns the same data structure as before — all consumers are unaffected.
 */
export async function fetchInstitutionHoldings(institution, options = {}) {
  const { includeHistory = false } = options;
  const cacheKey = `13f_${institution.cik}_${includeHistory ? 'history' : 'summary'}`;
  const cached = sessionStorage.getItem(cacheKey);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      if (parsed.timestamp && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed.data;
      }
    } catch {}
  }

  try {
    const filingCount = includeHistory ? 6 : 2;
    const res = await fetch(`/api/stock/13f?cik=${institution.cik}&filings=${filingCount}`);
    if (!res.ok) return fallbackInstitutionHoldings(institution.cik);
    const { filings } = await res.json();
    if (!filings || !filings.length) return fallbackInstitutionHoldings(institution.cik);

    const [latest, previous, ...older] = filings;

    // Comparison/metrics logic — unchanged from original
    const comparedHoldings = previous
      ? compareHoldings(latest.holdings, previous.holdings)
      : latest.holdings;

    const result = {
      ...latest,
      holdings: comparedHoldings,
      changeSummary: previous ? summarizeHoldingChanges(comparedHoldings, previous.holdings) : null,
      previousFilingDate: previous?.filingDate || null,
      sectorDistribution: buildSectorDistribution(comparedHoldings),
      trend: buildValueTrend([latest, previous, ...(includeHistory ? older : [])].filter(Boolean)),
    };

    sessionStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), data: result }));
    return result;
  } catch {
    return fallbackInstitutionHoldings(institution.cik);
  }
}

export { INSTITUTIONS };
